
import { computed, defineComponent, onMounted, reactive, ref, watch, WatchStopHandle } from 'vue';
import { CustomerRaffle, useRaffle } from '@/compositions/useRaffle';
import DialogQRCodeReader from '@/components/organisms/DialogQRCodeReader.vue';
import ComsbiSaasScheme from '@comsbi/common/src/libs/comsbi-saas-scheme';
import RafflePageContents from '@/components/organisms/raffle/RafflePageContents.vue';
import { useRoute, useRouter } from 'vue-router/composables';
import { ToastProgrammatic as Toast } from 'buefy';
import { objectFromEntries } from '@/utils';
import { RafflePrize } from '@comsbi/common/src/types/models';

export default defineComponent({
  name: 'PagesRaffle',

  components: { RafflePageContents, DialogQRCodeReader },

  setup(_) {
    const { resolve, giveTicket, drawOnline, drawOffline } = useRaffle();
    const route = useRoute();
    const router = useRouter();

    const refDialogQRCodeReader = ref<InstanceType<typeof DialogQRCodeReader>>();

    const state = reactive<{ id: string; data: CustomerRaffle | null; loading: boolean }>({
      id: route.params.id,
      data: null,
      loading: false,
    });

    const _loadContents = async () => {
      state.data = (await resolve(state.id)).result || null;
    };

    onMounted(async () => {
      // Set title

      await _loadContents();
    });

    watch(
      () => route,
      async (to, from) => {
        // パス更新
        state.id = to.params.id;
        await _loadContents();
      }
    );

    const showConfirmQR = ref(false);
    const tickets = ref(0);
    const attemptGetTickets = async (numTickets: number) => {
      //

      tickets.value = numTickets;
      showConfirmQR.value = true;
    };

    const drawContext = reactive({
      showConfirm: false,
      numOfDrawRequests: 1,
    });

    const master = computed(() => state.data?.raffle);

    const numOfUnusedTickets = computed(
      () => state.data?.contents.tickets.filter((item: any) => !item.used).length ?? 0
    );
    const maxOfTickets = computed(() => {
      return master.value?.settings.numOfTicketsForDraw ?? 0;
    });
    const numOfDraws = computed(() => {
      return Math.floor(numOfUnusedTickets.value / maxOfTickets.value);
    });

    const refRafflePageContents = ref<InstanceType<typeof RafflePageContents>>();
    const giveTicketsResult = reactive<{ show: boolean; tickets: number; shop: any }>({
      show: false,
      tickets: 0,
      shop: null,
    });
    const parseQRValue = async (value: string) => {
      if (!value) return;
      if (value.indexOf('cs:') !== 0) {
        // 無効なコード
        console.log('invalid value. value:' + value);

        Toast.open({
          message: 'QRコードの読み取りに失敗しました。',
          type: 'is-warning',
          position: 'is-top',
          duration: 15000, // CS-8253
        });
        return;
      }

      const [modelKey, modelId] = ComsbiSaasScheme.parse(value);
      const [_, queryStr = ''] = value.split('?');
      const query = objectFromEntries(
        queryStr
          .split('&')
          .filter(v => Boolean(v))
          .map(v => v.split('='))
      );
      console.log(modelKey, modelId, query);

      if (modelKey !== ComsbiSaasScheme.RAFFLE || modelId !== state.id) {
        //
        Toast.open({
          message: '抽選券用のQRコードを読み込んでください。',
          type: 'is-warning',
          position: 'is-top',
          duration: 15000, // CS-8253
        });
        return;
      }

      try {
        state.loading = true;

        // 付与処理
        const {
          result: { numTickets, shop },
        } = await giveTicket(modelId, tickets.value!, query.shop_ch);
        refRafflePageContents.value!.clearNumRequestTickets();

        //
        giveTicketsResult.tickets = numTickets;
        giveTicketsResult.shop = shop;
        giveTicketsResult.show = true;
        showConfirmQR.value = false; //
        state.loading = false;
        // 完了
        await _loadContents();
      } catch (e) {
        const _e = e as { response: { status: number } };
        const status: number = _e.response?.status;
        const message =
          {
            431: '抽選券はすでに付与済みです。',
            432: '抽選券は1日1回受け取れます。',
            433: 'この店舗の抽選券はすでに付与済みです。',
            434: 'この店舗の抽選券は1日1回受け取れます。',
          }[status] || '付与に失敗しました';
        const type =
          {
            431: 'is-warning',
            432: 'is-warning',
            433: 'is-warning',
            434: 'is-warning',
          }[status] || 'is-danger';

        Toast.open({
          message,
          type,
          position: 'is-top',
          duration: 15000, // CS-8253
        });
        // throw e;
      } finally {
        state.loading = false;
      }
    };

    const startQRCodeReader = async () => {
      refDialogQRCodeReader.value?.open();
    };

    //
    const drawResultOffline = reactive({ show: false, numOfDraws: 0 });
    const _flowDrawOffline = async () => {
      await drawOffline(state.id, drawContext.numOfDrawRequests);
      //
      drawResultOffline.numOfDraws = drawContext.numOfDrawRequests;
      drawResultOffline.show = true;
    };

    //
    const drawResultOnline = reactive<{
      show: boolean;
      showAnimation: boolean;
      results: { prize?: RafflePrize; consolationPrize?: RafflePrize }[];
    }>({
      show: false,
      showAnimation: false,
      results: [],
    });

    const _playAnimation = async () => {
      const { settings } = state.data!.raffle;
      const animSrc = settings.displayContents.drawAnimation ?? `${process.env.VUE_APP_CDN}/movie/treasureBox.mp4`;
      let unwatch: WatchStopHandle | null = null;
      try {
        drawResultOnline.showAnimation = true;
        await new Promise(resolve => setTimeout(resolve, 0));
        await new Promise((resolve, reject) => {
          const videoElm = document.getElementById('animation') as HTMLVideoElement;
          videoElm.style.opacity = '0';
          videoElm.oncanplay = () => {
            state.loading = false;
            videoElm.style.opacity = '1.0';
          };
          videoElm.onended = resolve;
          videoElm.onerror = reject;
          videoElm.src = animSrc;
          unwatch = watch(
            () => drawResultOnline.showAnimation,
            value => {
              if (value) return;
              if (unwatch) reject('closeModal');
            }
          );
        });
        state.loading = true;
      } catch (e) {
        console.warn(e);
      } finally {
        if (unwatch) {
          (unwatch as WatchStopHandle)();
          unwatch = null;
        }
        //
        setTimeout(() => (drawResultOnline.showAnimation = false), 100);
      }
    };

    const _flowDrawOnline = async () => {
      // アニメーション再生
      await _playAnimation();
      const [
        {
          result: { results },
        },
        _,
      ]: any[] = await Promise.all([
        // 抽選処理
        (async () => {
          await new Promise(resolve => setTimeout(resolve, 3500));
          return await drawOnline(state.id, drawContext.numOfDrawRequests);
        })(),
      ]);
      state.loading = false;
      // 結果表示
      console.info(results);
      // showOnlineResults.value = true;
      drawResultOnline.results = results;
      drawResultOnline.show = true;
    };

    const startDraw = async () => {
      if (!state.data) return;
      state.loading = true;
      drawContext.showConfirm = false;
      try {
        // 抽選
        const { settings } = state.data.raffle;
        if (settings.type === 'ONLINE') {
          await _flowDrawOnline();
        }
        if (settings.type === 'OFFLINE') {
          await _flowDrawOffline();
        }
        state.loading = false;
        // 完了
        await _loadContents();
        //
      } catch (e) {
        const _e = e as { response: { status: number } };
        const status: number = _e.response?.status;
        const message =
          {
            431: '抽選券はすでに付与済みです。',
            432: '抽選券は1日1回受け取れます。',
            433: 'この店舗の抽選券はすでに付与済みです。',
            434: 'この店舗の抽選券は1日1回受け取れます。',
            480: 'この抽選券はすでにコンプリート済みです。',
          }[status] || '抽選に失敗しました';
        const type =
          {
            431: 'is-warning',
            432: 'is-warning',
            433: 'is-warning',
            434: 'is-warning',
            480: 'is-warning',
            481: 'is-warning',
            482: 'is-warning',
          }[status] || 'is-danger';

        Toast.open({
          message,
          type,
          position: 'is-top',
          duration: 15000, // CS-8253
        });
        // throw e;
      } finally {
        state.loading = false;
      }
    };

    return {
      refDialogQRCodeReader,
      state,
      master,

      //
      showConfirmQR,
      drawContext,
      tickets,
      attemptGetTickets,
      showHistory: () => {
        router.push(`/raffle/${state.id}/history`);
      },

      numOfDraws,
      //
      startQRCodeReader,
      //
      refRafflePageContents,
      giveTicketsResult,
      parseQRValue,
      //

      startDraw,
      drawResultOnline,
      drawResultOffline,

      // debug
      router: { ...router },
    };
  },
});
